import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useFetch from 'use-http';
import { selectIsMaintenanceMode, setMaintenanceMode } from '../redux/slices/appState';

/**
 * @returns {{
 *   isMaintenanceMode: boolean,
 *   isChecking: boolean,
 *   retryCheck: () => Promise<void>
 * }}
 */
export const useMaintenanceModeCheck = () => {
  const isMaintenanceMode = useSelector(selectIsMaintenanceMode);
  const dispatch = useDispatch();
  const [isChecking, setIsChecking] = useState(!isMaintenanceMode);

  const { get, response, error } = useFetch('/health', {
    errorOn: { 5: false }, // tells this specific useFetch instance: "Do not automatically consider HTTP status codes in the 500-599 range as errors."
    retries: 0,
    persist: false, // disables caching
  });

  const performCheck = useCallback(async () => {
    console.log('Performing health check...');
    setIsChecking(true);
    await get();

    if (response.ok) {
      console.log('Health check OK.');
      if (isMaintenanceMode) {
        dispatch(setMaintenanceMode(false));
      }
    } else if (response.status === 503) {
      try {
        const cloned = response.clone();
        const body = await cloned.json();
        if (body?.code === 'MAINTENANCE_MODE_ENABLED') {
          console.warn('Health check detected maintenance mode.');
          if (!isMaintenanceMode) {
            dispatch(setMaintenanceMode(true));
          }
        } else {
          console.warn('Health check got 503, but not maintenance code.');
        }
      } catch (e) {
        console.error('Health check got 503, but failed to parse body:', e);
      }
    } else if (error) {
      console.error('Health check failed (network/other):', error);
    } else {
      console.warn('Health check received non-OK status:', response.status);
    }
    setIsChecking(false);
  }, [get, dispatch, isMaintenanceMode]);

  useEffect(() => {
    if (!isMaintenanceMode) {
      // Only run initial check if not already known to be in maintenance
      performCheck();
    }
  }, [performCheck]);

  const retryCheck = useCallback(async () => {
    await performCheck();
  }, [performCheck]);

  return { isMaintenanceMode, isChecking, retryCheck };
};
